define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../Util/array2Json',
    '../Util/json2Array',
    '../System/ColorObj',
    '../System/Vec3Obj',
    '../Enum/EnumObservationMode',
    '../Enum/EnumEditMode'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    array2Json,
    json2Array,
    ColorObj,
    Vec3Obj,
    EnumObservationMode,
    EnumEditMode
) {
    'use strict'

    /**
     * 通视分析。
     * <br /><br />
     * <div align='center'>
     * <img src='images/SightLineAnalysis.png' width='900' height='650' /><br />
     * 通视分析
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/SightLineAnalysis.html|CooWasm SightLineAnalysis demo}
     * @alias SightLineAnalysis
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumObservationMode} [props.observationMode=EnumObservationMode.DIVERGENT] 通视观察模式，默认：扩散。
     * @param {EnumEditMode} [props.editMode=EnumEditMode.EDIT] 编辑模式，当point、points不为空时，为SHOW。
     * @param {Vec3Obj} [props.point=null] 扩散模式下为观察点位置，聚合模式下为目标点位置。为空时，需要鼠标在场景中点选创建。
     * @param {Array.<Vec3Obj>} [props.points=null] 扩散模式下为目标点集，聚合模式下为观察点集。
     * @param {Number} [props.minDistance=1.0] 最小可视距离。
     * @param {Number} [props.maxDistance=100000.0] 最大可视距离。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @param {CallbackBoolean} [editEndCallback=null] 编辑模式结束回调函数。
     * @param {CallbackString} [tipsCallback=null] 提示文字回调函数。
     * @param {CallbackObj} [pointEditCallback=null] 编辑点回调函数。
     * @example
     * var polygon = new CooWasm.SightLineAnalysis({},(state)=>{console.log("load status:" + state)})
     */
    class SightLineAnalysis {
        /**
         * @description 通视观察模式，默认：扩散。
         * @type {EnumObservationMode}
         */
        observationMode
        /**
         * @description 编辑模式，当point、points不为空时，为SHOW。
         * @type {EnumEditMode}
         */
        editMode
        /**
        * @description 扩散模式下为观察点位置，聚合模式下为目标点位置。为空时，需要鼠标在场景中点选创建。
        * @type {Vec3Obj}
        */
        point
        /**
         * @description 扩散模式下为目标点集，聚合模式下为观察点集。
         * @type {Array.<Vec3Obj>}
         */
        points
        /**
         * @description 最小可视距离。
         * @type {Number}
         */
        minDistance
        /**
         * @description 最大可视距离。
         * @type {Number}
         */
        maxDistance
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
         * @description 通视分析相交点（世界坐标）结果。如无相交则为null。
         * @type {Array.<Vec3Obj>}
         * @readonly
         */
        intersectResult
        /**
         * @description 通视分析相交点（经纬度）结果。如无相交则为null。
         * @type {Array.<Vec3Obj>}
         * @readonly
         */
        intersectGeoResult
        /**
         * @description 通视分析相交点相对于线段比例结果。
         * @type {Array.<Number>}
         * @readonly
         */
        ratioResult
        /**
         * @description 显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true
        /**
         * @description 编辑模式结束回调函数。
         * @type {CallbackBoolean}
         */
        editEndCallback
        /**
         * @description 提示文字回调函数。
         * @type {CallbackString}
         */
        tipsCallback
        /**
        * @description 编辑点回调函数。
        * @type {CallbackObj}
        */
        pointEditCallback
        /**
         * @description 面对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 通视分析组件uuid。
         * @type {String}
         * @readonly
         */
        componentSightLineAnalysisUuid = null
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description SightLineAnalysis在CooWasm中类名。
         * @private
         * @readonly
         */

        className = "SightLineAnalysis"
        static avatarType = "deSMP.Avatar.EmptyAvatar"
        static componentSightLineAnalysisType = "AvatarComponent.AnalysisComponent.SightLineAnalysisComponent"
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"

        constructor(props, callback, editEndCallback, tipsCallback, pointEditCallback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            this.editEndCallback = defaultValue(editEndCallback, null)
            this.tipsCallback = defaultValue(tipsCallback, null)
            this.pointEditCallback = defaultValue(pointEditCallback, null)
            let bSync = true
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.EmptyAvatar"
            let componentCategoryName = SightLineAnalysis.componentSightLineAnalysisType + "#$%" + SightLineAnalysis.componentAltitudeVisCtrlType + "#$%"
            let avatarProps = ""
            let componentProps = ""
            //create avatars
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")

            let sightLineAnalysisComponentProps = ""
            if (defined(props.point)) {
                sightLineAnalysisComponentProps += this.toTuple3Str("point", props.point, "Point")
            }
            if (defined(props.points)) {
                this.points = props.points
                sightLineAnalysisComponentProps += "Points:" + array2Json("TUPLE3D", props.points) + "|"
            }
            sightLineAnalysisComponentProps += this.toStr("observationMode", props.observationMode, "ObservationMode")
            sightLineAnalysisComponentProps += this.toStr("editMode", props.editMode, "EditMode")
            sightLineAnalysisComponentProps += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
            sightLineAnalysisComponentProps += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
            componentProps += sightLineAnalysisComponentProps + "#$%"
            let altitudeVisCtrlComponentProps = ""
            altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
            altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")
            componentProps += altitudeVisCtrlComponentProps + "#$%"
            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(componentCategoryName), encode(componentProps), bSync, this.bVisible, true)
            //var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(encode(""), aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), this.bVisible, true)
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentSightLineAnalysisUuid = idArray[1]
                this.componentAltitudeVisCtrlUuid = idArray[2]
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback) && defined(this.points) && this.points.length > 0) {
                        callback(state)
                        Element.prototype.Map.sightLineAnalysiss.push(this)
                    }
                })
                callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(this.editEndCallback)) {
                        this.editEndCallback(state)
                    }
                })
                callbackSystem.register("Tips", this.avatarUuid, (info) => {
                    //this.getProperties()
                    if (defined(this.tipsCallback)) {
                        this.tipsCallback(info)
                    }
                })
                callbackSystem.register("PointEdit", this.avatarUuid, (obj) => {
                    this.getProperties()
                    if (defined(this.pointEditCallback)) {
                        const jsonObj = JSON.parse(obj)
                        this.pointEditCallback(jsonObj)
                    }
                })
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {EnumObservationMode} [props.observationMode] 通视观察模式。
         * @param {EnumEditMode} [props.editMode] 编辑模式。
         * @param {Vec3Obj} [props.point] 扩散模式下为观察点位置，聚合模式下为目标点位置。为空时，需要鼠标在场景中点选创建。
         * @param {Array.<Vec3Obj>} [props.points] 扩散模式下为目标点集，聚合模式下为观察点集。
         * @param {Number} [props.minDistance] 最小可视距离。
         * @param {Number} [props.maxDistance] 最大可视距离。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback] 加载成功后触发的函数回调。
         * @param {CallbackBoolean} [editEndCallback] 编辑模式结束回调函数。
         * @param {CallbackString} [tipsCallback] 提示文字回调函数，可不使用自带提示文字，方便更丰富的文字样式配置。
         * @param {CallbackObj} [pointEditCallback] 编辑点回调函数。
         * @example
         * polygon.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)})
         */
        update(props, callback, editEndCallback, tipsCallback, pointEditCallback) {
            let avatarProps = ""
            let componentProps = ""
            let sightLineAnalysisComponentProps = ""
            let altitudeVisCtrlComponentProps = ""
            let uuids = ""
            this.editEndCallback = defaultValue(editEndCallback, this.editEndCallback)
            this.tipsCallback = defaultValue(tipsCallback, this.tipsCallback)
            this.pointEditCallback = defaultValue(pointEditCallback, this.pointEditCallback)
            if (defined(props)) {
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")

                if (defined(props.point)) {
                    sightLineAnalysisComponentProps += this.toTuple3Str("point", props.point, "Point")
                }
                if (defined(props.points)) {
                    this.points = props.points
                    sightLineAnalysisComponentProps += "Points:" + array2Json("TUPLE3D", props.points) + "|"
                }
                sightLineAnalysisComponentProps += this.toStr("observationMode", props.observationMode, "ObservationMode")
                sightLineAnalysisComponentProps += this.toStr("editMode", props.editMode, "EditMode")
                sightLineAnalysisComponentProps += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
                sightLineAnalysisComponentProps += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
                //altitudeVisCtrlComponent
                altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
                altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")

                uuids += this.componentSightLineAnalysisUuid + "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"
                componentProps += sightLineAnalysisComponentProps + "#$%"
                componentProps += altitudeVisCtrlComponentProps + "#$%"
                callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(this.editEndCallback)) {
                        this.editEndCallback(state)
                    }
                })
                callbackSystem.register("Tips", this.avatarUuid, (info) => {
                    //this.getProperties()
                    if (defined(this.tipsCallback)) {
                        this.tipsCallback(info)
                    }
                })
                callbackSystem.register("PointEdit", this.avatarUuid, (obj) => {
                    this.getProperties()
                    if (defined(this.pointEditCallback)) {
                        const jsonObj = JSON.parse(obj)
                        this.pointEditCallback(jsonObj)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
                // Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentSpatialSightLineAnalysisUuid), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }

        toTuple3Str(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                const posStr = `{"x":${value.x},"y":${value.y},"z":${value.z}}`
                result = key + ":" + posStr + "|"
            }
            return result
        }

        toColorStr(memName, value, key) {
            let result = "";
            if (defined(value)) {
                this[memName] = value;
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toHexStr() + "|";
                } else {
                    if (typeof value === 'string') {
                        let color = ColorObj.fromCssColorString(value);
                        if (defined(color)) {
                            value = color.toHexStr();
                            this[memName] = value;
                            result = key + ":" + value + "|";
                        } else {
                            console.warn(`Invalid color value: ${value}. Using default value.`);
                        }
                    }
                }
            }
            return result;
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * SightLineAnalysis.locate((state)=>{console.log("locate status:" + state)})
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * SightLineAnalysis.getProperties()
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                if (pName == "SightLineAnalysisComponent") {
                    this.componentSightLineAnalysisUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "Point") {
                            this.point = Vec3Obj.fromStr(value)
                        } else if (name == "Points") {
                            this.points = json2Array(value)
                        } else if (name == "MinVisibleDistance") {
                            this.minDistance = Number(value)
                        } else if (name == "MaxVisibleDistance") {
                            this.maxDistance = Number(value)
                        } else if (name == "IntersectGeoPoints") {
                            this.intersectGeoResult = json2Array(value)
                        } else if (name == "IntersectPoints") {
                            this.intersectResult = json2Array(value)
                        } else if (name == "Ratios") {
                            this.ratioResult = json2Array(value)
                        } else if (name == "ObservationMode") {
                            this.observationMode = value
                        } else if (name == "EditMode") {
                            this.editMode = value
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * SightLineAnalysis = SightLineAnalysis.destroy((state)=>{console.log("destroy status:" + state)})
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.measures = Element.prototype.Map.measures.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("LoadAvatarAndComponentEx", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("EditEnd", this.avatarUuid)
                callbackSystem.remove("Tips", this.avatarUuid)
                callbackSystem.remove("PointEdit", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentSightLineAnalysisUuid = null
                this.componentAltitudeVisCtrlUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return SightLineAnalysis
})